/* The ranking's number format, shared by the home page's podium and the ranking page. The mock
   writes scores as "2 010 PTS" — thin-space groups, not the locale's dot, so the number reads the
   same to every player whatever their browser language is. */
export const formatPoints = (points) => String(points ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

// The list in ranking order: most points first. `sort` is stable, so a tie keeps the server's
// order, which breaks it by the older account (`backend:users/use_cases.RANKING_ORDER`).
export const rankedUsers = (users) => [...users].sort((a, b) => b.rankingPoints - a.rankingPoints)

// The ranking page's address, pointed at one player when there is one to point at.
export const rankingPath = (userId) => (userId == null ? "/ranking" : `/ranking?focus=${userId}`)

/* The leagues (owner, 2026-10-08): Bronze, Silver, Gold, Diamond by points, and Commander for the
   top places that reached Diamond. The server converts a player's points to a league
   (`backend:users/leaderboard.league_of`) and publishes the boundaries (`ranking_leagues` in GET
   /game-config/); these helpers only word them. */
export const COMMANDER = "commander"

// Each league's emoji, worn on its filter button and its badge.
export const LEAGUE_ICONS = { bronze: "🥉", silver: "🥈", gold: "🥇", diamond: "💎", [COMMANDER]: "🎖️" }

// The league's colour, as Ranking.css sets `--league`: the player card is lit in it.
export const LEAGUE_TONES = { bronze: "#d0905f", silver: "#b8c0c8", gold: "#e2b74a", diamond: "#5fc4ef", [COMMANDER]: "#ff6b2c" }

const ascending = (config) => [...(config?.leagues ?? [])].sort((a, b) => a.min_points - b.min_points)

// The league a number of points reaches, Commander aside: Commander also needs a top-ten place,
// which depends on everybody else. The result screen compares a player's league before and after
// a game with it, so a promotion is never claimed off a place the client cannot see.
export const leagueByPoints = (points, config) => {
  const leagues = ascending(config)
  if (!leagues.length || points == null) return null
  const reached = leagues.filter((league) => points >= league.min_points)
  return (reached.length ? reached[reached.length - 1] : leagues[0]).id
}

// Each league's span, lowest first, for the legend: `to` is null on the open-ended top league.
export const leagueRanges = (config) => {
  const leagues = ascending(config)
  return leagues.map((league, index) => ({
    id: league.id,
    from: league.min_points,
    to: index + 1 < leagues.length ? leagues[index + 1].min_points - 1 : null,
  }))
}

// The order the league filter lists them in, lowest first, Commander last (owner, 2026-10-08).
export const leagueOrder = (config) => {
  const ids = ascending(config).map((league) => league.id)
  return ids.length && config?.commander_top ? [...ids, COMMANDER] : ids
}

export const ALL_LEAGUES = "all"

// The page sizes `backend:users/leaderboard.PAGE_SIZES` accepts; a hundred rows by default (owner,
// 2026-10-08).
export const PER_PAGE_OPTIONS = [10, 20, 50, 100]
export const DEFAULT_PER_PAGE = 100

// The page buttons: the first and the last always, the current one and its two neighbours, and
// "…" for every gap.
export const GAP = "…"
export const pageNumbers = (page, pageCount) => {
  const out = []
  for (let n = 1; n <= pageCount; n += 1) {
    if (n === 1 || n === pageCount || Math.abs(n - page) <= 1) out.push(n)
    else if (out[out.length - 1] !== GAP) out.push(GAP)
  }
  return out
}
